import { useQuery, keepPreviousData } from "@tanstack/react-query";
import createPrivateAdminPanelInstance from "../../../api/PrivateAdminPanelInstance";
import { API_ROUTES } from "../../../api/ApiRoutes";

type StoredToken = { AccessToken?: string };

type AdminPanelInstance = {
  post: <T = unknown>(url: string, payload: unknown) => Promise<{ data?: T }>;
};
const createInstance = createPrivateAdminPanelInstance as unknown as (
  token?: string,
) => AdminPanelInstance;

export type WalletBalanceType =
  | "primary_currency_balance"
  | "primary_not_wagered_currency_balance"
  | "redeemable_cash_balance";

export type WalletBalanceOperator = "gte" | "lte" | "eq" | "gt" | "lt";

export type WalletSearchBy = "username" | "user_id" | "email";

export type WalletSortOrder = "asc" | "desc";

export type WalletBalanceFilter = {
  balance_type: WalletBalanceType;
  operator: WalletBalanceOperator;
  operand: number | null;
};

export type WalletTransactionItem = {
  user_id: string;
  username: string;
  email: string;
  name: string;
  registration_subregion: string | null;
  primary_currency_balance: number;
  redeemable_cash_balance: number;
  cash_balance_7d_change_pct: number | null;
};

export type WalletTransactionsPagination = {
  current_page: number;
  page_size: number;
  total_items: number;
  total_pages: number;
  has_next: boolean;
  has_prev: boolean;
};

export type WalletTransactionsParams = {
  page?: number;
  page_size?: number;
  sort_by?: string | null;
  sort_order?: WalletSortOrder | null;
  search_term?: string | null;
  search_by?: WalletSearchBy | null;
  balance_filters?: WalletBalanceFilter[];
  registration_subregion?: string | null;
};

type WalletApiResponse = {
  data?: WalletTransactionItem[] | null;
  pagination?: Partial<WalletTransactionsPagination>;
  filters_applied?: unknown;
};

export type WalletTransactionsResult = {
  transactions: WalletTransactionItem[];
  pagination: WalletTransactionsPagination;
  filters_applied: unknown;
};

const fetchWalletTransactions = async ({
  page,
  page_size,
  sort_by,
  sort_order,
  search_term,
  search_by,
  balance_filters,
  registration_subregion,
}: Required<Pick<WalletTransactionsParams, "page" | "page_size">> &
  Omit<
    WalletTransactionsParams,
    "page" | "page_size"
  >): Promise<WalletTransactionsResult> => {
  const tokenString = localStorage.getItem("token");
  const token: StoredToken | null = tokenString
    ? JSON.parse(tokenString)
    : null;
  const instance = createInstance(token?.AccessToken);

  const payload: Record<string, unknown> = {
    page,
    page_size,
  };

  Object.assign(payload, {
    ...(sort_by && { sort_by }),
    ...(sort_order && { sort_order }),
    ...(search_term && { search_term, ...(search_by && { search_by }) }),
    ...(balance_filters && balance_filters.length > 0 && { balance_filters }),
    ...(registration_subregion && { registration_subregion }),
  });

  const response = await instance.post<WalletApiResponse>(
    API_ROUTES.GET_WALLET_TRANSACTIONS,
    payload,
  );

  const transactions = response?.data?.data ?? [];
  const apiPagination = response?.data?.pagination;
  const pagination: WalletTransactionsPagination = {
    current_page: apiPagination?.current_page ?? page,
    page_size: apiPagination?.page_size ?? page_size,
    total_items: apiPagination?.total_items ?? transactions.length,
    total_pages:
      apiPagination?.total_pages ??
      (transactions.length ? Math.ceil(transactions.length / page_size) : 0),
    has_next: apiPagination?.has_next ?? false,
    has_prev: apiPagination?.has_prev ?? page > 1,
  };

  return {
    transactions,
    pagination,
    filters_applied: response?.data?.filters_applied ?? null,
  };
};

export const useGetWalletTransactions = ({
  page = 1,
  page_size = 50,
  sort_by = null,
  sort_order = null,
  search_term = null,
  search_by = null,
  balance_filters = [],
  registration_subregion = null,
}: WalletTransactionsParams = {}) => {
  return useQuery<WalletTransactionsResult, Error>({
    queryKey: [
      "GET_WALLET_TRANSACTIONS",
      page,
      page_size,
      sort_by,
      sort_order,
      search_term,
      search_by,
      JSON.stringify(balance_filters),
      registration_subregion,
    ],
    queryFn: () =>
      fetchWalletTransactions({
        page,
        page_size,
        sort_by,
        sort_order,
        search_term,
        search_by,
        balance_filters,
        registration_subregion,
      }),
    placeholderData: keepPreviousData,
    staleTime: 1000 * 60,
    refetchOnWindowFocus: false,
  });
};
